/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { type WindowsLocalGroup, type WindowsLocalGroupMember } from 'app/common/types/mo';
import { type Observable } from 'rxjs';

import { type WindowsGroupMemberTypeE } from './windows-local-access-user-group-str.service';

@Injectable({ providedIn: 'root' })
export class WindowsLocalAccessGroupService {
    constructor(private http: HttpService) { }

    /**
     * 本地认证用户组详情查询接口
     */
    get(RID: string, vstoreId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('WindowsLocalGroup.get')
            .setParams({
                RID,
                vstoreId,
            });

        return this.http.get(rest);
    }

    /**
     * 本地认证用户组批量查询接口
     */
    getBatchRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalGroup.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 本地认证用户组查询成员接口
     */
    getMemberRest(groupName: string, accountType: WindowsGroupMemberTypeE, vstoreId: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalGroupMap.getBatch');

        rest.setParams({
            GROUPNAME: groupName,
            ACCOUNTTYPE: accountType,
            vstoreId,
        });

        return rest;
    }

    /**
     * 本地认证用户组查询成员数量接口
     */
    getMemberCountRest(groupName: string, accountType: WindowsGroupMemberTypeE, vstoreId: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalGroupMap.count');

        rest.setFilters([{
            field: 'GROUPNAME',
            value: groupName,
            matchMode: '::',
        }, {
            field: 'ACCOUNTTYPE',
            value: accountType,
            matchMode: '::',
        }]);
        rest.setParams({ vstoreId });

        return rest;
    }

    /**
     * 本地认证用户组数量接口
     */
    countRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalGroup.count', undefined, true);

        if (vstoreId) {
            rest.setParams({ vstoreId });
        }

        return rest;
    }

    /**
     * 创建本地认证用户组
     */
    create(body: WindowsLocalGroup, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('WindowsLocalGroup.post');
        return this.http.post(rest, body, options);
    }

    /**
     * 新增用户组成员
     */
    addMember(body: WindowsLocalGroupMember, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('WindowsLocalGroupMap.add');
        return this.http.post(rest, body, options);
    }

    /**
     * 删除本地认证组用户
     */
    delete(name: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http
            .makeUrl('WindowsLocalGroup.delete')
            .setParams({
                NAME: name,
                vstoreId,
            });

        return this.http.delete(rest, options);
    }

    /**
     * 移除本地用户组成员
     */
    removeMember(member: WindowsLocalGroupMember, group: WindowsLocalGroup, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http
            .makeUrl('WindowsLocalGroupMap.remove')
            .setParams({
                NAME: member.NAME,
                ACCOUNTTYPE: member.ACCOUNTTYPE,
                GROUPNAME: group.NAME,
            });

        if (group.vstoreId) {
            rest.setParams({ vstoreId: group.vstoreId });
        }

        return this.http.delete(rest, options);
    }

    /**
     * 修改本地用户组
     */
    modify(body: WindowsLocalGroup, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('WindowsLocalGroup.modify');
        return this.http.put(rest, body, options);
    }

    // 获取所有多租户的Rest
    getVstoreRest(): Rest {
        return this.http.makeUrl('vstore.getBatch');
    }
}
